import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import type { JSX } from "react";
// eslint-disable-next-line import/no-namespace
import * as Flags from "country-flag-icons/react/3x2";
import { useAuth } from "../../../auth/store";
import { EmptyState, ErrorState } from "../../../components/States";
import { DataTable, PageHeader } from "../../../components/ui";
import { countryName } from "../../../lib/country";
import { insightsApi, insightsKeys, type LocationReport } from "../api";
import { StatTile } from "../charts";
import { Card, RangePicker, useRange } from "../components";
import { WorldMap } from "../map/WorldMap";
import "../traffic.css";

function CountryLabel({ code }: { code: string }) {
  const Flag = (Flags as Record<string, ((props: { "aria-hidden"?: boolean }) => JSX.Element) | undefined>)[code];
  return (
    <span className="traffic-country">
      {Flag ? <Flag aria-hidden /> : null}
      {countryName(code)}
    </span>
  );
}

function pct(share: number): string {
  return new Intl.NumberFormat(undefined, { style: "percent", maximumFractionDigits: 1 }).format(share);
}

function placeLabel(row: LocationReport["places"][number]): string {
  return [row.city, row.region].filter(Boolean).join(", ") || countryName(row.code);
}

export function TrafficPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { range } = useRange();
  const q = useQuery({
    queryKey: insightsKeys.locations(range, auth.currentProjectId),
    queryFn: () => insightsApi.locations(range),
    enabled: !!auth.currentProjectId,
  });
  const report = q.data;
  const openCountry = (code: string) => void navigate({ to: "/", search: { country: code } as never });

  return (
    <div className="viz traffic-page">
      <PageHeader title="Traffic" subtitle="Where sessions started, from the network address. The place is approximate." actions={<RangePicker />} />
      {!auth.currentProjectId ? (
        <EmptyState mark={null} title="Choose a project" hint="Traffic is counted per project." />
      ) : q.isError ? (
        <ErrorState error={q.error} retry={() => void q.refetch()} />
      ) : (
        <>
          <div className="viz-tiles" style={{ marginBottom: 16 }}>
            <StatTile label="Sessions" value={report?.sessions} loading={q.isLoading} hero />
            <StatTile label="With a location" value={report?.located} loading={q.isLoading} hero />
            <StatTile label="Countries" value={report?.countries.length} loading={q.isLoading} hero />
          </div>
          <Card title="Map">
            {q.isLoading ? <div className="skeleton" style={{ height: 280 }} /> : <WorldMap counts={report?.countries ?? []} places={report?.places ?? []} />}
            {report && report.sessions > report.located ? (
              <p className="muted small" style={{ marginTop: 8 }}>{(report.sessions - report.located).toLocaleString()} sessions have no location.</p>
            ) : null}
          </Card>
          <div className="traffic-tables">
            <Card title="Countries">
              <div className="stack-table">
                <DataTable
                  ariaLabel="Sessions by country"
                  loading={q.isLoading}
                  rows={report?.countries ?? []}
                  rowKey={(row) => row.code}
                  onRowClick={(row) => openCountry(row.code)}
                  empty={q.isLoading ? null : "No sessions in this range have a location yet."}
                  columns={[
                    { key: "country", header: "Country", label: "Country", render: (row) => <CountryLabel code={row.code} /> },
                    { key: "sessions", header: "Sessions", label: "Sessions", align: "right", render: (row) => row.sessions.toLocaleString() },
                    { key: "share", header: "Share", label: "Share", align: "right", render: (row) => pct(row.share) },
                  ]}
                />
              </div>
            </Card>
            <Card title="Cities and regions">
              <div className="stack-table">
                <DataTable
                  ariaLabel="Sessions by city and region"
                  loading={q.isLoading}
                  rows={report?.places ?? []}
                  rowKey={(row) => `${row.code}:${row.region}:${row.city}`}
                  onRowClick={(row) => openCountry(row.code)}
                  empty={q.isLoading ? null : "No city or region yet. Country is still shown on the map."}
                  columns={[
                    { key: "place", header: "Place", label: "Place", render: (row) => placeLabel(row) },
                    { key: "country", header: "Country", label: "Country", render: (row) => <CountryLabel code={row.code} /> },
                    { key: "sessions", header: "Sessions", label: "Sessions", align: "right", render: (row) => row.sessions.toLocaleString() },
                    { key: "share", header: "Share", label: "Share", align: "right", render: (row) => pct(row.share) },
                  ]}
                />
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
